import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2Icon, Loader2Icon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { FormField, inputClasses } from '../../components/ui/FormField';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
type Status = 'idle' | 'submitting' | 'success';
const quickLinks = [{
  to: '/faq',
  label: 'Frequently Asked Questions'
}, {
  to: '/warranty-claim',
  label: 'Start a warranty claim'
}, {
  to: '/order-tracking',
  label: 'Track an order'
}, {
  to: '/returns',
  label: 'Return & Refund Policy'
}, {
  to: '/shipping',
  label: 'Shipping Policy'
}, {
  to: '/warranty',
  label: 'Warranty Information'
}, {
  to: '/safety',
  label: 'Product Safety & Use'
}];
export function Support() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    order: '',
    topic: 'product',
    message: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<Status>('idle');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Please enter a valid email address.';
    if (form.message.trim().length < 10) next.message = 'Please tell us a little more (10+ characters).';
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus('submitting');
    track('contact_submit', {
      topic: form.topic
    });
    window.setTimeout(() => setStatus('success'), 700);
  };
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(f => ({
    ...f,
    [k]: e.target.value
  }));
  return <>
      <PageHeader title="Contact & Support" lead="Professional-grade lighting deserves professional-grade support. We're here to help before and after your purchase." data-id="element-1436" data-mp="2lucwttw36mym" />
      <div className="mx-auto grid max-w-site gap-10 px-5 py-12 md:px-8 md:py-16 lg:grid-cols-[1fr_1.4fr]" data-id="element-1437" data-mp="f7f15kyy8qik">
        <aside data-id="element-1438" data-mp="k0hubrztdxy8">
          <h2 className="font-display text-2xl font-semibold uppercase" data-id="element-1439" data-mp="k100bgca3qjc">Get help fast</h2>
          <ul className="mt-5 divide-y divide-white/10 border-y mf-hairline" data-id="element-1440" data-mp="69d2vd6lobyz">
            {quickLinks.map(l => <li key={l.to} data-id="element-1441" data-mp="a6raj0emubov">
                <Link to={l.to} className="block py-4 text-white hover:text-ice" data-id="element-1442" data-mp="xy6jhc0wzr93">{l.label}</Link>
              </li>)}
          </ul>
          <h2 className="mt-8 font-display text-2xl font-semibold uppercase" data-id="element-1443" data-mp="12x9ee3n6kh3c">Reach us</h2>
          <dl className="mt-4 space-y-3 text-sm" data-id="element-1444" data-mp="oxi2q9k4zcsi">
            <div data-id="element-1445" data-mp="twunmorg69sg"><dt className="text-titanium/70" data-id="element-1446" data-mp="twupkub0cjv3">Email</dt><dd data-id="element-1447" data-mp="tvjl0s9qn82x"><a href="mailto:support@marineforceled.com" className="text-ice hover:underline" data-id="element-1448" data-mp="tvjmkwpdzg51">support@marineforceled.com</a></dd></div>
          </dl>
        </aside>

        <div className="rounded-2xl border mf-hairline bg-navy p-6 md:p-10" data-id="element-1449" data-mp="3env87755pdew">
          {status === 'success' ? <div role="status" className="py-10 text-center" data-id="element-1450" data-mp="365a23b28q7a5">
              <CheckCircle2Icon className="mx-auto h-10 w-10 text-marine" data-id="element-1451" data-mp="324zuxv2ppatp" />
              <h2 className="mt-4 font-display text-3xl font-semibold uppercase" data-id="element-1452" data-mp="326sqofbseamx">Message received</h2>
              <p className="mt-2 text-titanium" data-id="element-1453" data-mp="3g24x3jzb1pjo">Thanks, {form.name.split(' ')[0]}. Our team will reply to {form.email}.</p>
            </div> : <form noValidate onSubmit={submit} className="grid gap-6 md:grid-cols-2" data-id="element-1454" data-mp="2ngv8qh4gah66">
              <FormField id="name" label="Name" error={errors.name} data-id="element-1455" data-mp="2ihicx6vlpzrg">
                <input className={inputClasses} value={form.name} onChange={set('name')} autoComplete="name" data-id="element-1456" data-mp="2x5mxkge7slcv" />
              </FormField>
              <FormField id="email" label="Email" error={errors.email} data-id="element-1457" data-mp="2sdvfyvd1dpyv">
                <input type="email" className={inputClasses} value={form.email} onChange={set('email')} autoComplete="email" data-id="element-1458" data-mp="24p2hos8xz613" />
              </FormField>
              <FormField id="order" label="Order number (optional)" data-id="element-1459" data-mp="1zpz9x01kulpf">
                <input className={inputClasses} value={form.order} onChange={set('order')} placeholder="#1001" data-id="element-1460" data-mp="12zx6dry1egwf" />
              </FormField>
              <FormField id="topic" label="Topic" data-id="element-1461" data-mp="17vumpy9avn97">
                <select className={inputClasses} value={form.topic} onChange={set('topic')} data-id="element-1462" data-mp="1bzag8lq2p7ho">
                  <option value="product" data-id="element-1463" data-mp="1gqsfn5cg700j">Product question</option>
                  <option value="sizing" data-id="element-1464" data-mp="1gs3ohjkr2xyi">Choosing a system</option>
                  <option value="order" data-id="element-1465" data-mp="1lotzhshp73d2">Order & shipping</option>
                  <option value="warranty" data-id="element-1466" data-mp="1qnng3tsa7ahg">Warranty</option>
                  <option value="other" data-id="element-1467" data-mp="1qq9n9sb9fnz5">Other</option>
                </select>
              </FormField>
              <div className="md:col-span-2" data-id="element-1468" data-mp="1vlozkftmdbtg">
                <FormField id="message" label="Message" error={errors.message} data-id="element-1469" data-mp="1zo3g2t9tfzhk">
                  <textarea rows={6} className={inputClasses} value={form.message} onChange={set('message')} data-id="element-1470" data-mp="1a0erx0pif15" />
                </FormField>
              </div>
              <div className="md:col-span-2" data-id="element-1471" data-mp="5d6jh0867y5i">
                <button type="submit" disabled={status === 'submitting'} className={`${buttonClasses('primary')} w-full sm:w-auto`} data-id="element-1472" data-mp="3tx8n1uo7gx6t">
                  {status === 'submitting' && <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-1473" data-mp="3p4fgd196fes3" />}
                  Send message
                </button>
              </div>
            </form>}
        </div>
      </div>
    </>;
}